<script setup lang="ts">

interface Props {
    color?: 'main' | 'secondary' | 'success' | 'info' | 'danger' | 'warning' | 'gray' | 'light' | 'white'
    type?: 'normal' | 'outline'
    size?: 'small' | 'normal' | 'large'
    design?: 'normal' | 'pill'
    shadow?: boolean,
    border?: boolean,
    disabled?: boolean
}

const variantClass = {
    main: {
        normal: 'bg-orange-600 border-orange-700/50 hover:bg-orange-700 focus:bg-orange-700 text-white',
        outline: 'border-orange-600 text-orange-600 hover:bg-orange-600 focus:bg-orange-600 hover:text-white focus:text-white'
    },
    secondary: {
        normal: 'bg-blue-600 border-blue-700/50 hover:bg-blue-700 focus:bg-blue-700 text-white',
        outline: 'border-blue-600 text-blue-600 hover:bg-blue-600 focus:bg-blue-600 hover:text-white focus:text-white'
    },
    success: {
        normal: 'bg-lime-500 border-lime-600/50 hover:bg-lime-600 focus:bg-lime-600 text-white',
        outline: 'border-lime-500 text-lime-500 hover:bg-lime-500 focus:bg-lime-500 hover:text-white focus:text-white'
    },
    info: {
        normal: 'bg-sky-400 border-sky-500/50 hover:bg-sky-500 focus:bg-sky-500 text-white',
        outline: 'border-sky-400 text-sky-400 hover:bg-sky-400 focus:bg-sky-400 hover:text-white focus:text-white'
    },
    danger: {
        normal: 'bg-red-500 border-red-600/50 hover:bg-red-600 focus:bg-red-600 text-white',
        outline: 'border-red-500 text-red-500 hover:bg-red-500 focus:bg-red-500 hover:text-white focus:text-white'
    },
    warning: {
        normal: 'bg-yellow-500 border-yellow-600/50 hover:bg-yellow-600 focus:bg-yellow-600 text-white',
        outline: 'border-yellow-500 text-yellow-500 hover:bg-yellow-500 focus:bg-yellow-500 hover:text-white focus:text-white'
    },
    gray: {
        normal: 'bg-gray-400 border-gray-500/50 hover:bg-gray-500 focus:bg-gray-500 text-white',
        outline: 'border-gray-400 text-gray-400 hover:bg-gray-400 focus:bg-gray-400 hover:text-white focus:text-white'
    },
    light: {
        normal: 'bg-gray-200 border-gray-300/50 hover:bg-gray-300 focus:bg-gray-300 text-gray-400',
        outline: 'border-gray-200 text-gray-400 hover:bg-gray-200 focus:bg-gray-200'
    },
    white: {
        normal: 'bg-white border-gray-50/50 hover:bg-gray-50 focus:bg-gray-50 text-gray-400',
        outline: 'border-gray-200 text-gray-400 hover:bg-gray-200 focus:bg-gray-200'
    }
}

const sizeClass = {
    small: 'px-3 py-1 text-sm',
    normal: 'px-5 py-2 text-base',
    large: 'px-7 py-3 text-xl'
}

const designClass = {
    normal: 'rounded-md',
    pill: 'rounded-full'
}

const {
    color = 'main',
    type = 'normal',
    size = 'normal',
    design = 'normal',
    shadow = true,
    border = true,
    disabled = false
} = defineProps<Props>()
</script>

<template>
    <button tabindex="-1" class="flex justify-center items-center gap-2 font-bold transition-all" :class="[
        variantClass[color][type],
        sizeClass[size],
        designClass[design],
        {
            'cursor-not-allowed': disabled,
            'shadow-[4px_4px_0_0_rgba(0,0,0,0.07)]': shadow && size === 'normal',
            'shadow-[2px_2px_0_0_rgba(0,0,0,0.07)]': shadow && size === 'small',
            'shadow-[6px_6px_0_0_rgba(0,0,0,0.07)]': shadow && size === 'large',
            'border-2': border
        }
    ]" :disabled="disabled">
        <slot></slot>
    </button>
</template>